import type { Ref } from 'vue';
import type { RouteParamsRaw } from 'vue-router';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ITableDataResponse, ITableMultiselectActions } from '@modules/tables/types';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import {
  isArray,
  isNumber,
  isObject,
  isString,
} from '@utils/guards';

import {
  AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS,
} from '../constants';
import { AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS } from '../submodules/fixed-tracker-payments/constants';

export interface ITrackerTableActionsComposable<T> {
  showCreateEditTrackerDialog: Ref<boolean>;
  showEditCountryDialog: Ref<boolean>;
  showChangeTrafficSourceDialog: Ref<boolean>;
  isTrackerCreating: Ref<boolean>;
  affiliateId: Ref<number | undefined>;
  trackerId: Ref<number | undefined>;
  trafficSourceId: Ref<number | undefined>;
  trackerDescription: Ref<string>;
  tableActions: Ref<ITableMultiselectActions>;
  onOpenTrackerDialog(): void;
  onTableActionItemSelect(optionId: string, listItem?: T): void;
  onHideCreateEditDialog(): void;
}

export default function useTrackerTableActions<ListItem>(
  tableData: Ref<ITableDataResponse<ListItem> | undefined>,
  editTrackerConditionRoute: symbol,
  editTrackerFixedPaymentsRoute: symbol,
): ITrackerTableActionsComposable<ListItem> {
  const showCreateEditTrackerDialog = ref(false);
  const showEditCountryDialog = ref(false);
  const showChangeTrafficSourceDialog = ref(false);
  const isTrackerCreating = ref(false);
  const affId = ref<number>();
  const trackerId = ref<number>();
  const trafficSourceId = ref<number>();
  const trackerDescription = ref<string>('');

  const route = useRoute();
  const router = useRouter();
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();

  const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : affId.value));

  const isColumnDescriptionAvailable = computed(() => tableData.value?.columns_for_display_data?.some(
    (column) => column.name === 'description',
  ));

  const isTrackerIdAvailable = computed(() => isArray(tableData.value?.list) && tableData.value?.list.every(
    (item) => isObject(item) && isNumber(item.id),
  ));

  const tableActions = computed<ITableMultiselectActions>(() => {
    const actions: ITableMultiselectActions = {};

    if (!isTrackerIdAvailable.value) {
      return actions;
    }

    if (isColumnDescriptionAvailable.value) {
      actions.editTrackerDescription = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_EDIT_TRACKER_DESCRIPTION').value,
        icon: 'pi-file-edit',
      };
    }

    if (hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.UPDATE_COUNTRY).value) {
      actions.editCountry = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_EDIT_TRACKER_COUNTRY').value,
        icon: 'pi-flag',
      };
    }

    if (hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.CHANGE_TRAFFIC_SOURCE).value) {
      actions.editTrafficSource = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_EDIT_TRAFFIC_SOURCE').value,
        icon: 'pi-sitemap',
      };
    }

    actions.editTrackerCondition = {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_EDIT_TRACKER_CONDITION').value,
      icon: 'pi-cog',
    };

    if (hasPermission(AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_EDIT).value) {
      actions.editFixedPayments = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_EDIT_FIXED_PAYMENTS').value,
        icon: 'pi-wallet',
      };
    }

    return actions;
  });

  function getRouteParams(listItem?: ListItem): RouteParamsRaw {
    const params: RouteParamsRaw = {};

    if (isObject(listItem) && isNumber(listItem.id)) {
      params.trackerId = listItem.id;
    }
    if (isObject(listItem) && isNumber(listItem.affiliate_id)) {
      params.id = listItem.affiliate_id;
    }

    return params;
  }

  function onTableActionItemSelect(optionId: string, listItem?: ListItem): void {
    switch (optionId) {
      case 'editTrackerDescription':
        onEditTracker(listItem);
        break;
      case 'editCountry':
        onEditCountry(listItem);
        break;
      case 'editTrafficSource':
        onChangeTrafficSource(listItem);
        break;
      case 'editTrackerCondition':
        void router.push({ name: editTrackerConditionRoute, params: getRouteParams(listItem) });
        break;
      case 'editFixedPayments':
        void router.push({ name: editTrackerFixedPaymentsRoute, params: getRouteParams(listItem) });
        break;
      default:
        break;
    }
  }

  function onOpenTrackerDialog(): void {
    isTrackerCreating.value = true;
    showCreateEditTrackerDialog.value = true;
  }

  function onEditTracker(listItem?: ListItem): void {
    if (isObject(listItem) && isNumber(listItem.affiliate_id)) {
      affId.value = listItem.affiliate_id;
    }
    if (isObject(listItem) && isNumber(listItem.id)) {
      trackerId.value = listItem.id;
    }
    if (isObject(listItem) && isString(listItem.description)) {
      trackerDescription.value = listItem.description;
    }
    showCreateEditTrackerDialog.value = true;
  }

  function onEditCountry(listItem?: ListItem): void {
    if (isObject(listItem) && isNumber(listItem.affiliate_id)) {
      affId.value = listItem.affiliate_id;
    }
    if (isObject(listItem) && isNumber(listItem.id)) {
      trackerId.value = listItem.id;
    }
    showEditCountryDialog.value = true;
  }

  function onChangeTrafficSource(listItem?: ListItem) {
    if (isObject(listItem) && isNumber(listItem.affiliate_id)) {
      affId.value = listItem.affiliate_id;
    }
    if (isObject(listItem) && isNumber(listItem.id)) {
      trackerId.value = listItem.id;
    }
    if (isObject(listItem) && isNumber(listItem.traffic_source_id)) {
      trafficSourceId.value = listItem.traffic_source_id;
    }
    showChangeTrafficSourceDialog.value = true;
  }

  function onHideCreateEditDialog(): void {
    affId.value = undefined;
    trackerId.value = undefined;
    trafficSourceId.value = undefined;
    trackerDescription.value = '';
    isTrackerCreating.value = false;
  }

  return {
    showCreateEditTrackerDialog,
    showEditCountryDialog,
    showChangeTrafficSourceDialog,
    isTrackerCreating,
    affiliateId,
    trackerId,
    trafficSourceId,
    trackerDescription,
    tableActions,
    onTableActionItemSelect,
    onOpenTrackerDialog,
    onHideCreateEditDialog,
  };
}
